import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, CreditCard, Mail } from 'lucide-react';
import './UsageLimitModal.css';

const SALES_MAILTO = 'mailto:sales@krime.app';

const STATIC_CONTENT = {
  searches: {
    title: "Monthly Search Limit Reached",
    body: "You've used all your location searches for this month. Upgrade to continue exploring new locations.",
  },
  historical_data: {
    title: "Upgrade to View Historical Data",
    body: "The Free plan includes the most recent year only. Upgrade to unlock the full historical archive.",
  },
  saved_searches: {
    title: "Upgrade to Save Search",
    body: "Save and quickly revisit your favorite locations with a paid plan.",
  },
  alerts: {
    title: "Upgrade to Set Alert",
    body: "Set up automatic alerts for any location with a paid plan.",
  },
  alert_limit: {
    title: "Alert Limit Reached",
    body: "You've reached the maximum number of active alerts for your plan. Upgrade to add more.",
  },
  export: {
    title: "Upgrade to Export Data",
    body: "Export is included on Captain and Chief plans. Upgrade to download your data as Text, CSV, Excel, PDF, or PNG.",
  },
};

// Tier-aware copy for export_limit / print_limit. Chief returns contactSales=true
// so the component swaps the upgrade button for a mailto link.
const getExportLimitCopy = (plan) => {
  if (plan === 'lieutenant') {
    return {
      title: "Upgrade to Export Data",
      body: "Export is included on Captain and Chief plans. Upgrade to download your data as Text, CSV, Excel, PDF, or PNG.",
    };
  }
  if (plan === 'captain') {
    return {
      title: "Monthly Output Limit Reached",
      body: "You've used all 400 of your combined exports and prints this month. Upgrade to Chief for 1,000 per month.",
    };
  }
  if (plan === 'chief') {
    return {
      title: "Monthly Output Limit Reached",
      body: "You've reached your monthly limit of 1,000 combined exports and prints. Contact us about Enterprise pricing.",
      contactSales: true,
    };
  }
  // free (or unknown) — treat like the existing gate-to-paid copy
  return {
    title: "Upgrade to Export Data",
    body: "Export is included on Captain and Chief plans. Upgrade to download your data as Text, CSV, Excel, PDF, or PNG.",
  };
};

const getPrintLimitCopy = (plan) => {
  if (plan === 'free') {
    return {
      title: 'Print Limit Reached',
      body: "You've used all 10 prints included on the Free plan. Upgrade for more.",
    };
  }
  if (plan === 'lieutenant') {
    return {
      title: "Monthly Print Limit Reached",
      body: "You've used all 100 prints this month. Upgrade for more prints or to download your data as Text, CSV, Excel, PDF, or PNG.",
    };
  }
  if (plan === 'captain') {
    return {
      title: "Monthly Output Limit Reached",
      body: "You've used all 400 of your combined exports and prints this month. Upgrade to Chief for 1,000 per month.",
    };
  }
  if (plan === 'chief') {
    return {
      title: "Monthly Output Limit Reached",
      body: "You've reached your monthly limit of 1,000 combined exports and prints. Contact us about Enterprise pricing.",
      contactSales: true,
    };
  }
  return {
    title: "Monthly Print Limit Reached",
    body: "You've used all your prints this month. Upgrade for more.",
  };
};

const FREE_SEARCH_LIMIT = {
  title: 'Search Limit Reached',
  body: "You've used all 5 location searches to get started on the Free plan. Upgrade to continue exploring new locations.",
};

const resolveContent = (reason, plan) => {
  if (reason === 'export_limit') return getExportLimitCopy(plan || 'free');
  if (reason === 'print_limit') return getPrintLimitCopy(plan || 'free');
  if (reason === 'searches' && plan === 'free') return FREE_SEARCH_LIMIT;
  return STATIC_CONTENT[reason] || STATIC_CONTENT.searches;
};

const UsageLimitModal = ({ show, reason = 'searches', plan = 'free', onUpgrade, onClose }) => {
  useEffect(() => {
    if (!show) return;
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      onClose();
      setTimeout(() => document.activeElement?.blur(), 0);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [show, onClose]);

  if (!show) return null;

  const content = resolveContent(reason, plan);
  const showContactSales = !!content.contactSales;

  return createPortal(
    <div className="usage-limit-overlay" onClick={onClose}>
      <div className="usage-limit-modal" onClick={e => e.stopPropagation()}>
        <button className="usage-limit-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        <div className="usage-limit-icon-wrap">
          <Lock size={28} className="usage-limit-icon" />
        </div>

        <h2 className="usage-limit-title">{content.title}</h2>
        <p className="usage-limit-body">{content.body}</p>

        {showContactSales ? (
          <a
            className="usage-limit-upgrade-btn"
            href={SALES_MAILTO}
            onClick={onClose}
          >
            <Mail size={16} />
            Contact Sales
          </a>
        ) : (
          <button className="usage-limit-upgrade-btn" onClick={onUpgrade}>
            <CreditCard size={16} />
            View Plans &amp; Upgrade
          </button>
        )}
        <button className="usage-limit-later-btn" onClick={onClose}>
          Maybe Later
        </button>
      </div>
    </div>,
    document.body
  );
};

export default UsageLimitModal;
